import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';

const EVENT_META = {
  KYC_CHANGE:      { label: 'KYC Change',       color: '#f59e0b', icon: '🔍' },
  SANCTIONS_HIT:   { label: 'Sanctions Hit',     color: '#ef4444', icon: '🚨' },
  DEAL_CLOSED:     { label: 'Deal Closed',       color: '#22c55e', icon: '📁' },
  DOCUMENT_UPLOAD: { label: 'Document Upload',   color: '#3b82f6', icon: '📎' },
  ROLE_CHANGE:     { label: 'Role Change',       color: '#a855f7', icon: '🔐' },
};

function EventBadge({ type }) {
  const m = EVENT_META[type] || { label: type, color: '#607060', icon: '📋' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
      background: m.color + '20', color: m.color,
    }}>
      {m.icon} {m.label}
    </span>
  );
}

function HashChip({ hash }) {
  return (
    <code style={{
      fontSize: 10, fontFamily: 'monospace', color: 'var(--text3)',
      background: 'var(--bg-surface)', padding: '2px 6px', borderRadius: 4,
      letterSpacing: '0.02em',
    }}>
      {hash ? hash.slice(0, 12) + '…' : '—'}
    </code>
  );
}

function VerifyBanner({ status, loading, onVerify }) {
  if (loading) {
    return (
      <div style={{
        padding: '14px 20px', borderRadius: 8, marginBottom: 20,
        background: '#3b82f620', border: '1px solid #3b82f640',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <span style={{ fontSize: 20 }}>⏳</span>
        <span style={{ color: '#3b82f6', fontWeight: 600 }}>Verifying chain integrity…</span>
      </div>
    );
  }
  if (!status) {
    return (
      <div style={{
        padding: '14px 20px', borderRadius: 8, marginBottom: 20,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ color: 'var(--text3)', fontSize: 13 }}>
          Chain integrity not yet verified.
        </span>
        <button className="btn btn-ghost btn-sm" onClick={onVerify}>
          🔗 Verify Chain
        </button>
      </div>
    );
  }
  const ok = status.status === 'OK';
  return (
    <div style={{
      padding: '14px 20px', borderRadius: 8, marginBottom: 20,
      background: ok ? '#22c55e20' : '#ef444420',
      border: `1px solid ${ok ? '#22c55e40' : '#ef444440'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 20 }}>{ok ? '✅' : '🚨'}</span>
        <div>
          <div style={{ fontWeight: 700, color: ok ? '#22c55e' : '#ef4444', fontSize: 14 }}>
            {ok ? 'Chain Integrity Verified' : 'Chain Integrity FAILED'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            {ok
              ? `${status.entries_checked} entries checked — no tampering detected`
              : `Tampered at seq #${status.first_invalid_seq}: ${status.reason}`}
          </div>
        </div>
      </div>
      <button className="btn btn-ghost btn-sm" onClick={onVerify}>
        🔄 Re-verify
      </button>
    </div>
  );
}

export default function AuditChainPage() {
  const [entries, setEntries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [verifyResult, setVerify]   = useState(null);
  const [verifying, setVerifying]   = useState(false);
  const [eventFilter, setEventFilter] = useState('');
  const [skip, setSkip]             = useState(0);
  const [hasMore, setHasMore]       = useState(false);
  const LIMIT = 100;

  const load = useCallback(async (newSkip = 0) => {
    setLoading(true);
    try {
      const params = { limit: LIMIT + 1, skip: newSkip };
      if (eventFilter) params.event_type = eventFilter;
      const r = await API.get('/audit-chain', { params });
      setHasMore(r.data.length > LIMIT);
      setEntries(r.data.slice(0, LIMIT));
      setSkip(newSkip);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  }, [eventFilter]);

  useEffect(() => { load(0); }, [load]);

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const r = await API.get('/audit-chain/verify');
      setVerify(r.data);
    } catch { setVerify({ status: 'FAIL', reason: 'Request failed', entries_checked: 0 }); }
    finally { setVerifying(false); }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Tamper-Evident Audit Chain</h1>
          <p>SHA-256 hash-chained log of all critical compliance events</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleVerify} disabled={verifying}>
          🔗 Verify Chain
        </button>
      </div>

      <VerifyBanner status={verifyResult} loading={verifying} onVerify={handleVerify} />

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          className="form-input"
          style={{ width: 200 }}
          value={eventFilter}
          onChange={e => setEventFilter(e.target.value)}
        >
          <option value="">All Event Types</option>
          {Object.entries(EVENT_META).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="card-title">
          🔗 Chain Entries
          {entries.length > 0 && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>
              {entries.length}{hasMore ? '+' : ''} entries
            </span>
          )}
        </div>

        {loading ? (
          <div className="loading">Loading audit chain…</div>
        ) : entries.length === 0 ? (
          <div style={{ color: 'var(--text3)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
            No chain entries yet. Events are written automatically on KYC changes, sanctions hits, deal closures, document uploads, and role changes.
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Seq</th>
                <th>Timestamp</th>
                <th>User</th>
                <th>Event</th>
                <th>Entity</th>
                <th>Payload</th>
                <th>Hash</th>
                <th>Prev Hash</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id}>
                  <td>
                    <code style={{ fontSize: 12, color: 'var(--accent)' }}>#{e.seq}</code>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                    {e.timestamp ? new Date(e.timestamp).toLocaleString('de-DE') : '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>{e.username}</td>
                  <td><EventBadge type={e.event_type} /></td>
                  <td style={{ fontSize: 11, color: 'var(--text2)' }}>
                    {e.entity_type && (
                      <span>{e.entity_type}{e.entity_id ? <span style={{ color: 'var(--text3)' }}> · {e.entity_id.slice(0, 8)}…</span> : ''}</span>
                    )}
                  </td>
                  <td style={{ maxWidth: 240 }}>
                    <details>
                      <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--text3)' }}>
                        {Object.keys(e.payload || {}).length} fields
                      </summary>
                      <pre style={{
                        fontSize: 10, marginTop: 4, padding: '6px 8px',
                        background: 'var(--bg-surface)', borderRadius: 4,
                        color: 'var(--text2)', overflow: 'auto', maxHeight: 120,
                      }}>
                        {JSON.stringify(e.payload, null, 2)}
                      </pre>
                    </details>
                  </td>
                  <td><HashChip hash={e.hash} /></td>
                  <td><HashChip hash={e.prev_hash} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {(skip > 0 || hasMore) && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            {skip > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={() => load(Math.max(0, skip - LIMIT))}>
                ← Previous
              </button>
            )}
            {hasMore && (
              <button className="btn btn-ghost btn-sm" onClick={() => load(skip + LIMIT)}>
                Next →
              </button>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="card" style={{ marginTop: 0 }}>
        <div className="card-title">Event Types</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {Object.entries(EVENT_META).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <EventBadge type={k} />
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                {k === 'KYC_CHANGE' && 'Counterparty KYC status updated'}
                {k === 'SANCTIONS_HIT' && 'Sanctions screening returned a hit'}
                {k === 'DEAL_CLOSED' && 'Deal stage changed to completed/cancelled'}
                {k === 'DOCUMENT_UPLOAD' && 'File attached to an entity'}
                {k === 'ROLE_CHANGE' && 'Admin changed a user\'s role'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
